import * as THREE from 'three';
import { PlanogramContainer } from "./PlanogramContainer.js";
import { PlanogramCamera } from "./PlanogramCamera.js";
import { PlanogramCameraFrame } from "./PlanogramCameraFrame.js";
import { PlanogramRendererResources, PlanogramRendererAttributes } from './Resources.js';
import { PlanogramObject } from './PlanogramObject.js';
import { PlanogramTextController } from './PlanogramText.js'
import { PlanogramBlockingDivider } from './PlanogramBlockingDivider.js';
import { PlanogramLabelController } from './PlanogramLabelController.js';

export class PlanogramRenderer extends HTMLElement {

    //#region Attributes 

    static Attributes = PlanogramRendererAttributes.Attributes;
    static DeveloperAttributes = PlanogramRendererAttributes.DeveloperAttributes;

    static get observedAttributes() {
        return [
            ...Object.values(PlanogramRenderer.Attributes),
            ...Object.values(PlanogramRenderer.DeveloperAttributes)
        ];
    }
    //#endregion

    //#region Private properties

    //#region Scene and Renderer 
    #firstRender = true;
    #shadowRoot;
    /** @type { THREE.WebGLRenderer } */
    #renderer;
    /** @type { HTMLDivElement } */
    #statusBar;
    /** @type { number } */
    #frameNumber;
    //#endregion

    //#endregion

    //#region Attribute Properties
    get showStatusBar() { return this.hasAttribute(PlanogramRenderer.Attributes.ShowStatusBar); }
    get disableSelect() { return this.hasAttribute(PlanogramRenderer.Attributes.DisableSelect); }
    get selectionColour() {
        return this.hasAttribute(PlanogramRenderer.Attributes.SelectionColour) ?
            this.getAttribute(PlanogramRenderer.Attributes.SelectionColour) : "rgb( 255, 223, 0)";
    }
    get hideTabs() { return this.hasAttribute(PlanogramRenderer.Attributes.HideTabs); }
    get disableTabDrag() { return this.hasAttribute(PlanogramRenderer.Attributes.DisableTabDrag); }
    get tabActiveBehaviour() { return this.getBehaviour(PlanogramRenderer.Attributes.TabActiveBehaviour); }
    get splitCameraBehaviour() { return this.getBehaviour(PlanogramRenderer.Attributes.SplitCameraBehaviour); }
    get joinCameraBehaviour() { return this.getBehaviour(PlanogramRenderer.Attributes.JoinCameraBehaviour); }
    get wireframePositions() { return this.hasAttribute(PlanogramRenderer.Attributes.WireframePositions); }
    get ignorePositions() { return this.hasAttribute(PlanogramRenderer.Attributes.IgnorePositions); }
    get ignoreSubcomponents() { return this.hasAttribute(PlanogramRenderer.Attributes.IgnoreSubcomponents); }
    get includeBlocking() { return this.hasAttribute(PlanogramRenderer.Attributes.IncludeBlocking); }
    get chestWallTransparency() {
        if (this.hasAttribute(PlanogramRenderer.Attributes.ChestWallTransparency))
            return Number(this.getAttribute(PlanogramRenderer.Attributes.ChestWallTransparency));
        return 1;
    }
    get interactInFocus() { return this.hasAttribute(PlanogramRenderer.Attributes.InteractInFocus); }
    // 0 is uncapped
    get frameCap() {
        var value = Number(this.getAttribute(PlanogramRenderer.Attributes.FrameCap));
        return isNaN(value) ? 0 : value;
    }
    get showFrameRate() { return this.hasAttribute(PlanogramRenderer.Attributes.ShowFrameRate); }
    get constrainToFace() { return this.hasAttribute(PlanogramRenderer.Attributes.ConstrainToFace); }
    get hangingGap() { return Number(this.getAttribute(PlanogramRenderer.Attributes.HangingGap)) ?? 0; }
    get snappingDistance() { return Number(this.getAttribute(PlanogramRenderer.Attributes.SnappingDistance)) ?? 0; }
    //#endregion

    //#region Developer Attribute Properties
    get showMessages() { return this.hasAttribute(PlanogramRenderer.DeveloperAttributes.ShowMessages); }
    get showBoundingBoxes() { return this.hasAttribute(PlanogramRenderer.DeveloperAttributes.ShowBoundingBoxes); }
    //#endregion

    //#region Properties
    get renderer() { return this.#renderer; }
    get domElement() { return this.#renderer.domElement; }
    /**@type {PlanogramCamera} */
    get focusedCamera() { return this.querySelector("planogram-camera[active][focused]"); }
    get focusedContainer() { return this.focusedCamera?.container; }
    /**@type {PlanogramCamera[]} */
    get activeFrameCameras() {
        const planogramCameraFrames = [...this.querySelectorAll(`planogram-camera-frame`)];
        const cameras = [];
        for (var i = 0; i < planogramCameraFrames.length; i++) {
            /**@type {PlanogramCameraFrame} */
            const planogramCameraFrame = planogramCameraFrames[i];
            if (Reflect.has(planogramCameraFrame, "associatedCameras")) {
                const activeCamera = planogramCameraFrame.associatedCameras.find(x => x.active);
                if (activeCamera) cameras.push(activeCamera);
            }
        }
        return cameras;
    }
    /**@type {HTMLDivElement} */
    renderView = null;

    ctrlKey = false;
    shiftKey = false;
    /**@type {PlanogramRendererResources|null} */
    resources = null;

    /**@type {PlanogramTextController|null} */
    textController = null;

    /**@type {PlanogramLabelController|null} */
    labelController = null;

    #flashInterval = null;
    //#region performance
    #stamp = new Date();
    /**@type {DOMHighResTimeStamp} */
    #fpsTime = null;
    /**@type {HTMLDivElement} */
    #fpsCounter = null;
    #sample = [];
    //#endregion
    //#endregion

    constructor() {
        super();

        this.#shadowRoot = this.attachShadow({ mode: 'open', slotAssignment: 'manual' });
        new CSSStyleSheet().replace(PlanogramRendererResources.RendererStyle).
            then(sheet => this.#shadowRoot.adoptedStyleSheets = [sheet]);

        this.ticks = 0;
        this.resources = new PlanogramRendererResources();
        this.textController = new PlanogramTextController(this.resources.TextShader, this);
        this.labelController = new PlanogramLabelController(this.resources.TextShader, this);
    }

    //#region Scene Methods

    #toggleStatusBar() {
        if (!this.showStatusBar) this.#statusBar.classList.add("hidden");
        else this.#statusBar.classList.remove("hidden");
    }

    #setUpRenderer() {
        THREE.ColorManagement.enabled = true;

        var container = document.createElement('div');
        container.className = 'rendererContainer';



        this.#statusBar = document.createElement("div");
        this.#statusBar.classList.add("statusBar");
        this.#toggleStatusBar();

        this.renderView = document.createElement("div");
        this.renderView.className = "renderView";

        this.#renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
        this.#renderer.setSize(1, 1, false);
        this.#renderer.setPixelRatio(window.devicePixelRatio);
        this.#renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
        this.#renderer.domElement.classList.add("renderer");

        this.renderView.appendChild(this.#renderer.domElement);
        container.appendChild(this.renderView);
        container.appendChild(this.#statusBar);
        this.#shadowRoot.appendChild(container);
    }

    #setUpFrameCounter() {
        if (this.showFrameRate && !this.#fpsCounter) {
            var container = document.querySelector(".renderView");
            if (container === null) return;
            this.#fpsCounter = document.createElement("div");
            this.#fpsCounter.style = "position:absolute;right:0;top:0;backgrournd-color:grey;z-index:10";
            container.appendChild(this.#fpsCounter);
        } else if (this.showFrameRate && this.#fpsCounter) {
            this.#fpsCounter.remove();
        }
    }

    /**
     * 
     * @param {DOMHighResTimeStamp} timeStamp 
     */
    #renderCycle(timeStamp) {

        var time = new Date().getTime() - this.#stamp.getTime();
        if (this.#sample.push(1000 / (performance.now() - this.#fpsTime)) > 50) this.#sample.shift();
        if (time > 1000 && this.#sample.length === 50) {
            if (this.#fpsCounter) this.#fpsCounter.innerText = (this.#sample.reduce((a, c) => a + (isNaN(c) ? 0 : c), 0) / 50).toFixed(0);
            this.#stamp = new Date();
        }
        this.#fpsTime = performance.now();

        this.#frameNumber = requestAnimationFrame(a => this.#renderCycle(a));
        var domElement = this.#renderer.domElement;
        var size = this.#renderer.getSize(new THREE.Vector2());
        if (size.x !== domElement.offsetWidth || size.y !== domElement.offsetHeight) {
            this.#renderer.setSize(domElement.offsetWidth, domElement.offsetHeight, false);
        }

        // const rowLength = cameraDocks.length;
        var i = 0;
        for (i; i < this.activeFrameCameras.filter(x => x.dockVector !== null).length; i++) {
            const planogramCamera = this.activeFrameCameras[i];
            const dockVector = planogramCamera.dockVector;
            const viewportHeight = this.domElement.getBoundingClientRect().height;
            const width = dockVector.z;
            const height = dockVector.w;
            const x = dockVector.x;
            const y = viewportHeight - height - dockVector.y;
            if (planogramCamera.camera instanceof THREE.OrthographicCamera) {
                planogramCamera.camera.left = width / -2;
                planogramCamera.camera.right = width / 2;
                planogramCamera.camera.top = height / 2;
                planogramCamera.camera.bottom = height / -2;
                planogramCamera.camera.updateProjectionMatrix();
            } else if (planogramCamera.camera instanceof THREE.PerspectiveCamera) {
                planogramCamera.camera.aspect = width / height;
                planogramCamera.camera.updateProjectionMatrix();
            }
            planogramCamera.syncLockVerticalLayout();
            planogramCamera.syncMiniMapLayout();
            this.#renderer.setViewport(x, y, width, height);
            this.#renderer.setScissor(x, y, width, height);
            this.#renderer.setScissorTest(true);
            var planogram = planogramCamera.container;
            if (planogram) {
                this.#renderer.render(planogram.scene, planogramCamera.camera);
            }
        }
        if (i === 0) {
            const emptyScene = new THREE.Scene();
            emptyScene.background = new THREE.Color(0xffffff);
            this.#renderer.setViewport(0, 0, domElement.offsetWidth, domElement.offsetHeight);
            this.#renderer.setScissor(0, 0, domElement.offsetWidth, domElement.offsetHeight);
            this.#renderer.render(emptyScene, new THREE.PerspectiveCamera());
        }

    }

    renderScene() {
        if (this.activeFrameCameras.filter(x => x.dockVector !== null).length)
            this.#renderer.render(this.focusedContainer.scene, this.focusedCamera.camera);
    }

    updateSlots() {
        // const slot = this.shadowRoot.querySelector("slot");
        // this.querySelectorAll("planogram-container").forEach(container=>{
        //     slot.assign(container);
        // });
    }

    //#endregion

    //#region Interaction Methods
    #getCameraUnderCursor(x, y) {
        const cameraUnderCursor = this.activeFrameCameras.find(camera => {
            const dockVector = camera.dockVector;
            return dockVector && x >= dockVector.x && x <= dockVector.x + dockVector.z &&
                y >= dockVector.y && y <= dockVector.y + dockVector.w;
        });
        return cameraUnderCursor;
    }

    #setupInteractions() {
        this.#renderer.domElement.addEventListener("mousedown", this.#mouseDown.bind(this));
        this.#renderer.domElement.addEventListener("mouseup", this.#mouseUp.bind(this));
        this.#renderer.domElement.addEventListener("mousemove", this.#mouseMove.bind(this));
        this.#renderer.domElement.addEventListener("dragstart", this.#dragStart.bind(this));
        this.#renderer.domElement.addEventListener("dragenter", this.#dragEnter.bind(this));
        this.#renderer.domElement.addEventListener("dragleave", this.#dragLeave.bind(this));
        this.#renderer.domElement.addEventListener("dragover", this.#dragOver.bind(this));
        this.#renderer.domElement.addEventListener("drop", this.#drop.bind(this));
        this.#renderer.domElement.addEventListener("pointerdown", this.#pointerDown.bind(this));
        this.#renderer.domElement.addEventListener("pointermove", this.#pointerMove.bind(this));
        this.#renderer.domElement.addEventListener("pointerup", this.#pointerUp.bind(this));
        this.#renderer.domElement.addEventListener("pointercancel", this.#pointerCancel.bind(this));
    }
    // #endregion

    //#region Events

    /**
     * 
     * @param {DragEvent} evt 
     */
    #dragStart(evt) {
        this.focusedContainer?.setDrag(evt.offsetX, evt.offsetY, true);
        evt.dataTransfer.setDragImage(new Image(), 0, 0);
        evt.dataTransfer.effectAllowed = "move";
        evt.dataTransfer.dropEffect = "move";
    }


    /**
     * 
     * @param {DragEvent} evt 
     */
    #dragEnter(evt) {
        this.focusedContainer?.constructDragArray(evt.dataTransfer);
        if (this.focusedContainer) {
            evt.preventDefault();
            evt.stopPropagation();
        }
    }

    /**
     * 
     * @param {DragEvent} evt 
     */
    #drop(evt) {
        evt.preventDefault();
        this.focusedContainer?.clearDragArray(true);
    }

    /**
     * 
     * @param {DragEvent} evt 
     */
    #dragOver(evt) {
        evt.preventDefault();
        this.focusedContainer?.setDrag(evt.offsetX, evt.offsetY);
        this.ctrlKey = evt.ctrlKey;
        this.shiftKey = evt.shiftKey;
    }

    #dragLeave() {
        this.focusedContainer?.clearDragArray(false);
    }

    #mouseMove(evt) {
        if (this.focusedContainer) {
            const options = this.focusedContainer.overObject(evt.offsetX, evt.offsetY);
            var cursor = options?.cursor ?? "default";
            if (options?.object instanceof PlanogramBlockingDivider) {
                const cameraWithinCursor = this.#getCameraUnderCursor(evt.offsetX, evt.offsetY);
                if (cameraWithinCursor?.type === "Orthographic")
                    cursor = options.object.direction === "vertical" ? "ew-resize" : "ns-resize";
                else if (cameraWithinCursor?.type === "Perspective")
                    cursor = "grab";
            }

            this.#renderer.domElement.style.cursor = cursor;
            this.#renderer.domElement.draggable = options?.isDraggable ?? false;
        }

    }
    #mouseDown(down) {
        const cameraWithinCursor = this.#getCameraUnderCursor(down.offsetX, down.offsetY);
        const targetIsCurrent = cameraWithinCursor === this.focusedCamera;

        if (!targetIsCurrent) {
            this.focusedCamera?.raiseBlurEvent();
            cameraWithinCursor?.raiseFocusEvent();
        }

        if (!this.disableSelect && down instanceof MouseEvent && down.target === this.#renderer.domElement && (down.button === 0 || down.button === 2) && (!this.interactInFocus || targetIsCurrent)) {
            this.focusedContainer?.onMouseDown(down.offsetX, down.offsetY, down.button === 0);
        }
    }

    /**
     * 
     * @param {MouseEvent} up 
     */
    #mouseUp(up) {
        if (!this.disableSelect && up instanceof MouseEvent && up.target === this.#renderer.domElement && (up.button === 0 || up.button === 2) && (!this.interactInfocus || targetIsCurrent))
            this.focusedContainer?.onMouseUp(up.offsetX, up.offsetY, up.button === 2);
    }

    /**
     * @param {PointerEvent} evt
     */
    #pointerDown(evt) {
        if (evt.button === 0 && evt.target === this.#renderer.domElement) {
            this.focusedContainer?.onPointerDown(evt.offsetX, evt.offsetY, evt.pointerId);
        }
    }

    /**
     * @param {PointerEvent} evt
     */
    #pointerMove(evt) {
        if (evt.target === this.#renderer.domElement) {
            this.focusedContainer?.onPointerMove(evt.offsetX, evt.offsetY, evt.pointerId);
        }
    }

    /**
     * @param {PointerEvent} evt
     */
    #pointerUp(evt) {
        if (evt.target === this.#renderer.domElement) {
            this.focusedContainer?.onPointerUp(evt.offsetX, evt.offsetY, evt.pointerId);
        }
    }

    /**
     * @param {PointerEvent} evt
     */
    #pointerCancel(evt) {
        if (evt.target === this.#renderer.domElement) {
            this.focusedContainer?.onPointerCancel(evt.pointerId);
        }
    }
    //#endregion

    //#region WebComponent Camera Docking Methods

    getBehaviour(attribute) {
        if (!this.hasAttribute(attribute)) return PlanogramRenderer.behaviour.none;
        var behaviour = this.getAttribute(attribute).toLocaleLowerCase();
        var key = Object.keys(PlanogramRenderer.behaviour).find(x => x.toLocaleLowerCase() === behaviour);
        return key ? PlanogramRenderer.behaviour[key] : PlanogramRenderer.behaviour.none;
    }

    blurCameras(camera) {
        this.querySelectorAll("planogram-camera").forEach(x => {
            if (x !== camera) { x.focused = false; }
        });
    }

    resetAllActiveCameras() {
        /**@type {PlanogramCamera[]} */
        const cameras = [...this.querySelectorAll("planogram-camera[active]")];
        cameras.forEach(x => {
            x.resetCamera();
            x.updateZoom(null, true);
        });
    }
    //#endregion

    //#region WebComponent Lifecycle CallBacks
    connectedCallback() {
        this.addEventListener("click", e => {
            if (e.button === 2 && e instanceof PointerEvent) {
                e.preventDefault();
                e.stopPropagation();
            }
        }, true);

        this.setAttribute("data-ignore-context", "ignore");
        this.#flashInterval = setInterval(() => {
            PlanogramRendererResources.SelectBoxShader.uniforms["borderColour"].value =
            new THREE.Color((this.toOrFrom = !this.toOrFrom) ? this.selectionColour : "rgb( 0, 0, 0 )").convertLinearToSRGB();
        }, 500);
        this.#setUpRenderer();
        this.#setUpFrameCounter();
        this.#renderCycle();
        this.#firstRender = false;
        this.#setupInteractions();
    }

    disconnectedCallback() {
        // this.#disposeMovementControls();
        this.#renderer.dispose();
        cancelAnimationFrame(this.#frameNumber);
        if (this.#flashInterval) clearInterval(this.#flashInterval);
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (this.#firstRender || oldValue === newValue) return;
        switch (name) {
            case PlanogramRenderer.Attributes.ShowStatusBar:
                this.#toggleStatusBar();
                break;
            case PlanogramRenderer.DeveloperAttributes.ShowBoundingBoxes:
                [...this.children].forEach(x => x.showBoundingBox = this.showBoundingBoxes);
                break;
            case PlanogramRenderer.Attributes.WireframePositions:
                [...this.querySelectorAll("planogram-position")].forEach(x => x.transparencyChanged());
                break;
            case PlanogramRenderer.Attributes.ChestWallTransparency:
                [...this.querySelectorAll("planogram-component")].forEach(x => x.subComponentPlug());
                break;
            case PlanogramRenderer.Attributes.HideTabs:
                [...this.querySelectorAll("planogram-camera-frame")].forEach(x => x.showHideTabBar(this.hideTabs));
                break;
            case PlanogramRenderer.Attributes.DisableTabDrag:
                [...this.querySelectorAll("planogram-camera")].forEach(x => x.setTabDraggable());
                break;
            case PlanogramRenderer.Attributes.ShowFrameRate:
                this.#setUpFrameCounter();
                break;
        }
    }
    //#endregion

    //#region Static properties
    static behaviour = {
        none: 'none',
        reset: 'reset',
        fit: 'fit',
        both: 'both',
    }

    //#endregion

    //#region Static Methods
    /**
     * 
     * @param {PlanogramObject|PlanogramProduct} element 
     * @returns {PlanogramRenderer|null}
     */
    static get(element) {
        var parent = element.parentElement;
        while (parent !== null && !(parent instanceof PlanogramRenderer)) {
            parent = parent.parentElement;
        }
        return parent;
    }
    /**
     * 
     * @param {object} item 
     * @param {Object.<string, string>} customEvents 
     */
    static applyToRelay(item, customEvents) {
        if (Reflect.has(document, "blazorRelay"))
            document.blazorRelay.applyRelay(item, customEvents);
    }
    //#endregion
}